/**
 * Primitivos de microanimação compartilhados (Fase 6, Plano de Ataque R8).
 *
 * Regra que atravessa este arquivo: toda animação aqui anima a APRESENTAÇÃO
 * de um valor/registro que já é real (já calculado pelos seletores do
 * dashboard/CRM) — nunca inventa número, tendência ou histórico que a fonte
 * de dados não publica. Sparkline por indicador e "tendência vs. período
 * anterior" ficaram FORA do escopo desta fase por este motivo: nenhuma fonte
 * hoje publica série histórica por indicador (`Kpi` tem só o valor atual) —
 * ver `docs/phase-reports/phase-06-microanimacoes-primitivos.md`.
 *
 * `prefers-reduced-motion` já colapsa toda duração CSS para ~0 globalmente
 * (`apps/plataforma/src/styles/tailwind.css`, `@layer base`) — os primitivos
 * CSS daqui (`s2-row-in`/`s2-bar-grow`, ver `styles.css`) herdam isso de
 * graça. O hook de contagem é JS, então confere a preferência ele mesmo.
 */
import { useEffect, useRef, useState } from "react";

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Anima um número até `target` com easing suave (contagem do dashboard).
 * `null` passa direto, sem animação — nunca anima "ausência de fonte" como
 * se fosse zero (`formatKpiValue` já trata `null` como "—", o estado
 * `awaiting_source` não passa por aqui). Na primeira montagem anima de 0; em
 * atualizações seguintes (refetch) anima do valor já exibido, não de 0 de
 * novo — um refresh de dado real não deve parecer um indicador zerando.
 */
export function useCountUp(target: number | null, durationMs = 700): number | null {
  const [value, setValue] = useState<number | null>(target);
  const frameRef = useRef<number | null>(null);
  const displayedRef = useRef<number | null>(target);
  const mountedRef = useRef(false);

  useEffect(() => {
    if (target === null) {
      displayedRef.current = null;
      mountedRef.current = true;
      setValue(null);
      return;
    }
    if (prefersReducedMotion()) {
      displayedRef.current = target;
      mountedRef.current = true;
      setValue(target);
      return;
    }
    const from = mountedRef.current ? displayedRef.current ?? 0 : 0;
    const to = target;
    mountedRef.current = true;
    const start = performance.now();
    function tick(now: number): void {
      const progress = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - progress, 3);
      const next = from + (to - from) * eased;
      displayedRef.current = next;
      setValue(next);
      if (progress < 1) frameRef.current = requestAnimationFrame(tick);
    }
    frameRef.current = requestAnimationFrame(tick);
    return () => {
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    };
  }, [target, durationMs]);

  return value;
}

/**
 * Atraso de entrada escalonada por índice de linha (classe `.s2-row-in` em
 * `styles.css`), com teto para uma lista longa não demorar a terminar de
 * aparecer inteira.
 */
export function rowStaggerDelayMs(index: number, stepMs = 40, maxMs = 320): number {
  return Math.min(index * stepMs, maxMs);
}
